import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface BottomSheetProps {
  title: string;
  /** Segunda linea de la cabecera: de que producto o equipo se esta hablando. */
  subtitle?: string;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Panel que sube desde el borde inferior.
 *
 * Es donde el chofer trabaja. Se abre pegado al pulgar, ocupa el ancho
 * completo y tapa lo que hay detras, de modo que mientras se confirma una
 * linea no hay ninguna otra cosa que pulsar. La alternativa —desplegar el
 * formulario dentro de la lista— empuja hacia abajo todo lo que viene despues
 * y obliga a buscar con el dedo el campo que acaba de aparecer.
 *
 * Atrapa el foco mientras esta abierto, se cierra con `Escape` y devuelve el
 * foco al control que lo abrio, igual que el panel deslizante del
 * administrador.
 *
 * Se dibuja en `document.body` mediante un portal, y no donde se monta.
 *
 * `position: fixed` y `z-index` no bastan: un ancestro con `position: sticky`
 * y `z-index` propio —la barra superior del administrador, sin ir mas lejos—
 * abre un contexto de apilado, y dentro de el el `z-40` del panel solo ordena
 * frente a sus hermanos. El contexto entero se pintaba en `z-20`, por debajo
 * del `z-30` del sidebar, asi que el panel salia con la mitad izquierda tapada
 * y las etiquetas cortadas. En el panel del chofer no se notaba porque alli no
 * hay nada al lado que lo tape.
 *
 * Colgando del `body` el panel no depende de donde lo monte quien lo usa.
 */
export function BottomSheet({
  title,
  subtitle,
  isOpen,
  onClose,
  children,
}: BottomSheetProps): ReactNode {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, isOpen, onClose);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Cerrar el panel"
        className="absolute inset-0 h-full w-full bg-content/40"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        /*
          Ancho limitado y centrado. En un teléfono el tope queda por encima
          del ancho de la pantalla, así que el panel sigue ocupándola entera
          como se diseñó; en el escritorio del administrador, sin tope, los
          campos se estiraban dos mil píxeles.
        */
        className="absolute inset-x-0 bottom-0 mx-auto max-h-[88dvh] w-full max-w-2xl overflow-y-auto rounded-t-sheet bg-surface shadow-lg"
      >
        {/* El asidero no es decorativo: dice que el panel llega desde abajo y se puede cerrar. */}
        <div className="flex justify-center pt-2">
          <span aria-hidden="true" className="h-1 w-10 rounded-full bg-line" />
        </div>

        <header className="flex items-start justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-content">{title}</h2>
            {subtitle ? <p className="mt-0.5 truncate text-sm text-muted">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onClose}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-card text-muted hover:bg-primary-soft"
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
          </button>
        </header>

        <div className="space-y-4 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
